<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive } from "vue";
import { getKennungTitle, getKennungValue } from "@/DataHelper";
import { mountedBase, onSave, BaseSetup, onDelete } from "@/DataLoaderStatic";
import { getSiteData } from "@/NavigationHelper";

import { showSnackbar } from "@/UIHelper";
import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits(["back", "save"]);

let clearable = ref<boolean>(false);

let showEssen = ref<boolean>(false);
let showKredit = ref<boolean>(false);
let showBezahlt = ref<boolean>(false);
let showKst = ref<boolean>(false);
let saveVisible = ref<boolean>(false);

let setKey = ref<number>(0);

let bezeichnung: string = "";
// Override für die Spalte "INVOICE_DATE", um sie explizit bearbeitbar zu machen
let gueltigkeitCol: any = null;

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_neuerBeleg_automBelegErkennung",
  aic: AUApi.Current.user.aic,
  backOnSave: true,
  emitBack: () => emit("back"),
};
const siteData = getSiteData();
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  pageParams.queryAIC = siteData.aic;
  pageParams.aic = siteData.aic;

  await mountedBase(uiData, pageParams, false);

  if (!uiData.showLoading && uiData.data) {
    const row = uiData.data[0];
    let bezahlt = false;
    bezahlt = getKennungTitle("BELEG_BEREITSFIRMABEZAHLT", uiData.columns);
    bezeichnung = "in Bearbeitung";

    if (bezahlt) {
      showBezahlt.value = true;
    }
    let kredit = false;

    kredit = getKennungTitle("BELEG_FIRMENKREDITKARTE", uiData.columns);

    if (kredit) {
      showKredit.value = true;
    }
    const essen = getKennungTitle("RK_ANZ_PERS", uiData.columns);
    if (essen) {
      showEssen.value = true;
    }
    const kostenstelle = getKennungTitle("KOSTENSTELLENZUORDNG", uiData.columns);
    if (kostenstelle) {
      showKst.value = true;
    }

    const bezColumn = uiData.columns.find((c: any) => c.kennung === "BEZEICHNUNG");
    gueltigkeitCol = uiData.columns.find((c: any) => c.kennung === "INVOICE_DATE");
    if (bezColumn) {
      const text = getKennungValue(row, "BEZEICHNUNG", uiData.columns);
      if (!text) {
        uiData.data[0][bezColumn.name] = "in Bearbeitung";
      }
    }
    if (gueltigkeitCol) {
      // Sicherstellen, dass der DatePicker nicht durch Metadaten gesperrt ist
      if (gueltigkeitCol.meta) {
        gueltigkeitCol.meta.readOnly = false;
        gueltigkeitCol.meta.invisible = false;
      }
      uiData.data[0][
        gueltigkeitCol.name
      ] = uiData.zeitbereich.von.toISOStringWithTZ().substr(0, 10);
    }
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
function checkChanged(val: any) {
  saveVisible.value = true;
  setKey.value += +1;
}
async function onSaveBeleg() {
  const { valid, invalidFields } = await validator.validateAsync();
  if (!valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" + invalidFields.join(", ") + ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  // achtung wird von den Reisen und vom MA direkt auch aufgerufen!
  let aic = siteData.aicReise ?? AUApi.Current.user.aic;
  if (!siteData.reloadData) {
    siteData.reloadData = true;
  }
  let nodeRequest = uiData.saveNodeRequest ?? "";
  if (nodeRequest) {
    aic = 0;
  }

  onSave(uiData, pageParams, 0, "", undefined, aic, true, [], nodeRequest)?.then(() => {
    saveVisible.value = false;
    setKey.value += +1;

    emit("back");
  });
}
async function bewDelete() {
  const siteData = getSiteData();
  if (uiData.data && uiData.data[0].aic_Bew_pool) {
    await onDelete(uiData, pageParams, uiData.data[0].aic_Bew_pool, "");
    if (!siteData.reloadData) {
      siteData.reloadData = true;
    }
    emit("back");
  } else if (uiData.data && uiData.data[0].aic_Stamm) {
    if (confirm("Wollen Sie den Datensatz wirklich - mit allen Kostensätzen löschen?")) {
      await onDelete(uiData, pageParams, uiData.data[0].aic_Stamm, "", undefined, true);
    }

    if (!siteData.reloadData) {
      siteData.reloadData = true;
    }
    emit("back");
  }
}
</script>
<template>
  <v-card :loading="uiData.showLoading">
    <v-card-text v-if="uiData.initialized">
      <EditTextField v-model="bezeichnung" kennung="BEZEICHNUNG" />

      <EditDatePicker
        @changeValue="checkChanged"
        :readonly="false"
        :queryColumn="gueltigkeitCol"
        :queryZeitbereich="pageParams.zeitbereich"
      />
      <EditComboBox
        v-if="showKst"
        :disabled="true"
        :queryZeitbereich="pageParams.zeitbereich"
        kennung="KOSTENSTELLENZUORDNG"
      />
      <EditSwitchButton
        @changeValue="checkChanged"
        v-if="showBezahlt"
        kennung="BELEG_BEREITSFIRMABEZAHLT"
      />

      <EditSwitchButton
        @changeValue="checkChanged"
        v-if="showKredit"
        kennung="BELEG_FIRMENKREDITKARTE"
      />
      <EditTextArea
        :clearable="clearable"
        kennung="MEMO_MEMO"
        @changeValue="checkChanged"
      />
      <EditComboBox
        :disabled="true"
        :queryZeitbereich="pageParams.zeitbereich"
        kennung="MA_RELATION"
      />
      <EditCheckBox kennung="RK_AUTOM_BELEGERKENNUNG" />
      <EditFileChoicer kennung="ABLAGE" @changeValue="checkChanged" />
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :key="setKey"
        v-if="uiData.initialized"
        :deleteVisible="false"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        @delete="bewDelete"
        :saveVisible="saveVisible"
        @save="onSaveBeleg"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.dvhCol {
  padding-right: 10px;
  padding-left: 10px;
  padding-top: 0px;
  padding-bottom: 0px;
}
</style>
